import { Color } from '@fe/core';
import { ChartPosition, Rect } from '../util';
import { DrawPass, ViewState } from './chart';
import { ScaleSpecification } from './chart-props';
import { getAxisRect } from './helpers';
import { Scale } from './scale';

export class Axis {
    private readonly scale = new Scale();
    private props: ScaleSpecification;

    constructor(props: ScaleSpecification) {
        this.props = props;
        this.setProps(props);
    }

    getProps() {
        return this.props;
    }

    draw(pass: DrawPass) {
        const [min, max] = this.scale.getMinMax();
        const rect = getAxisRect(this.props.position, pass);

        if (this.props.axisLabels) {
            if (this.props.axisLabels.type === 'text') {
                drawTextAxisScale(
                    pass,
                    rect,
                    { project: (v) => this.project(v, pass.viewState), min, max },
                    this.props.position,
                    this.props.axisLabels
                );
            } else if (this.props.axisLabels.type === 'custom') {
                this.props.axisLabels.draw(pass, rect, {
                    project: (v) => this.project(v, pass.viewState),
                    min,
                    max,
                    args: this.props.axisLabels.args
                });
            }
        }
    }

    isVertical() {
        return this.props.position === ChartPosition.right || this.props.position === ChartPosition.left;
    }

    isHorizontal() {
        return !this.isVertical();
    }

    setProps(props: ScaleSpecification) {
        this.props = props;
        if (!props.autoScale) {
            this.scale.setLimits(props.min, props.max);
        }
    }

    project(value: number, viewState?: ViewState) {
        if (!viewState) {
            return this.scale.project(value);
        }

        if (this.isVertical()) {
            return viewState.chartViewport.top + viewState.translateY + (1 - this.scale.project(value)) * viewState.chartArea.height;
        } else {
            return viewState.chartViewport.left + viewState.translateX + this.scale.project(value) * viewState.chartArea.width;
        }
    }

    unproject(value: number, viewState?: ViewState) {
        if (!viewState) {
            return this.scale.unproject(value);
        }

        if (this.isVertical()) {
            return (
                this.scale.getMax() -
                this.scale.unproject(value - viewState.chartViewport.top - viewState.translateY) /
                    viewState.chartViewport.height /
                    viewState.scaleY
            );
        } else {
            return this.scale.unproject(
                (value - viewState.chartViewport.left - viewState.translateX) / viewState.chartViewport.width / viewState.scaleX
            );
        }
    }

    getMax() {
        return this.scale.getMax();
    }

    getMin() {
        return this.scale.getMin();
    }

    setLimits(min: number, max: number) {
        if (!this.props.autoScale) {
            throw new Error('Axis is not set to auto scale.');
        }

        this.scale.setLimits(min, max);
    }
}
export type CustomAxisDrawFunction<T> = (
    pass: DrawPass,
    rect: Rect,
    args: { project: (value: number) => number; min: number; max: number; args: T }
) => void;

function drawTextAxisScale(
    pass: DrawPass,
    rect: Rect,
    args: { project: (value: number) => number; min: number; max: number },
    position: ChartPosition,
    props: Extract<ScaleSpecification['axisLabels'], { type: 'text' }>
) {
    const labelStep =
        typeof props.labelStep === 'number'
            ? props.labelStep
            : typeof props.labelStep === 'function'
              ? props.labelStep(args.min, args.max, pass.viewState.chartArea.height)
              : null;

    const bigTickStep =
        typeof props.bigTick === 'number'
            ? props.bigTick
            : typeof props.bigTick === 'function'
              ? props.bigTick(args.min, args.max, pass.viewState.chartArea.height)
              : null;

    const smallTickStep =
        typeof props.smallTick === 'number'
            ? props.smallTick
            : typeof props.smallTick === 'function'
              ? props.smallTick(args.min, args.max, pass.viewState.chartArea.height)
              : null;

    const isHorizontal = position === ChartPosition.top || position === ChartPosition.bottom;

    const fixedPosition =
        position === ChartPosition.left
            ? rect.right
            : position === ChartPosition.right
              ? rect.left
              : position === ChartPosition.top
                ? rect.bottom
                : position === ChartPosition.bottom
                  ? rect.top
                  : 0;

    if (labelStep) {
        for (let i = args.min; i <= args.max; i += labelStep) {
            const x = isHorizontal ? args.project(i) : fixedPosition + labelOffset[position];
            const y = isHorizontal ? fixedPosition + labelOffset[position] : args.project(i);
            if (!rect.contains(x, y)) {
                continue;
            }
            pass.drawer.drawText(
                props.label ? props.label(i) : i.toString(),
                [x, y],
                {
                    baseline: isHorizontal ? (position === ChartPosition.top ? 'bottom' : 'top') : 'middle',
                    alignment: isHorizontal ? 'center' : position === ChartPosition.left ? 'end' : 'start'
                },
                {
                    color: props.color as Color
                }
            );
        }
    }

    if (bigTickStep) {
        for (let i = args.min; i <= args.max; i += bigTickStep) {
            const x1 = isHorizontal ? args.project(i) : fixedPosition;
            const y1 = isHorizontal ? fixedPosition : args.project(i);
            const x2 = isHorizontal ? args.project(i) : fixedPosition + bigTickOffset[position];
            const y2 = isHorizontal ? fixedPosition + bigTickOffset[position] : args.project(i);
            if (!rect.contains(x1, y1)) {
                continue;
            }

            pass.drawer.drawLine([x1, y1], [x2, y2], {
                color: (props.bigTickColor ?? props.color) as Color,
                width: 1
            });
        }
    }

    if (smallTickStep) {
        for (let i = args.min; i <= args.max; i += smallTickStep) {
            // Small tick overlaps with big tick
            if (bigTickStep && (i - args.min) % bigTickStep === 0) {
                continue;
            }
            const x1 = isHorizontal ? args.project(i) : fixedPosition;
            const y1 = isHorizontal ? fixedPosition : args.project(i);
            const x2 = isHorizontal ? args.project(i) : fixedPosition + smallTickOffset[position];
            const y2 = isHorizontal ? fixedPosition + smallTickOffset[position] : args.project(i);
            if (!rect.contains(x1, y1)) {
                continue;
            }
            pass.drawer.drawLine([x1, y1], [x2, y2], {
                color: (props.smallTickColor ?? props.color) as Color,
                width: 1
            });
        }
    }
}

export const distanceAxis: CustomAxisDrawFunction<{ textColor: Color; pattern: [Color, Color]; offset: number }> = (pass, rect, args) => {
    const step = calculateDistanceSteps(args.max);
    const drawRect = new Rect([rect.left, rect.top], { width: rect.width, height: rect.height }).translate(0, args.args.offset);
    pass.drawer.drawRect(
        new Rect([drawRect.left, drawRect.bottom - 10], { width: drawRect.width, height: 8 }),
        {
            color: '#fff'
        },
        {
            width: 0.5,
            color: '#000'
        }
    );

    for (let i = 0; i <= args.max; i += step) {
        const x1 = args.project(i);
        const y = drawRect.bottom - 10;
        if (!drawRect.contains(x1, y)) {
            continue;
        }
        pass.drawer.drawText(
            i.toString(),
            [x1, y],
            {
                alignment: 'center',
                baseline: 'bottom'
            },
            {
                color: args.args.textColor
            }
        );
    }

    pass.drawer.beginClip(drawRect);
    let even = true;
    for (let i = 0; i < args.max; i += step) {
        const x1 = args.project(i);
        const y = drawRect.bottom - 10;
        const x2 = args.project(Math.min(args.max, i + step));
        if (drawRect.contains(x1, y) || drawRect.contains(x2, y)) {
            pass.drawer.drawRect(new Rect([x1 + 1, y + 1], { width: x2 - x1 - 2, height: 6 }), {
                color: args.args.pattern[even ? 0 : 1]
            });
        }
        even = !even;
    }
    pass.drawer.endClip();
};

function calculateDistanceSteps(distance: number): number {
    let step;
    switch (true) {
        case distance >= 6000:
            step = 1000;
            break;
        case distance >= 4000:
            step = 500;
            break;
        case distance >= 2000:
            step = 250;
            break;
        case distance >= 1000:
            step = 100;
            break;
        default:
            step = 50;
    }
    return step;
}

const labelOffset: Record<ChartPosition, number> = {
    [ChartPosition.top]: -15,
    [ChartPosition.right]: 15,
    [ChartPosition.bottom]: 15,
    [ChartPosition.left]: -15
};

const bigTickOffset: Record<ChartPosition, number> = {
    [ChartPosition.top]: -10,
    [ChartPosition.right]: 10,
    [ChartPosition.bottom]: 10,
    [ChartPosition.left]: -10
};

const smallTickOffset: Record<ChartPosition, number> = {
    [ChartPosition.top]: -5,
    [ChartPosition.right]: 5,
    [ChartPosition.bottom]: 5,
    [ChartPosition.left]: -5
};
